/**
 * "Assign to <person>" — offered for someone @mentioned in the text who is not yet the
 * assignee.
 *
 * This writes the SAME `assigneeUserId` field the assignee picker writes; it is a shortcut
 * into that field, not a second way to assign. Mentioning a colleague never assigns on its
 * own — you always take this one explicit action — so referencing someone in passing can't
 * silently make them the owner of the work.
 */
import React from 'react';
import { Button } from '@lognai/synapse-design-system-v2';
import { UserPlus } from '@lognai/synapse-design-system-v2/icons';
import { getCurrentUser, type MentionedUser } from '@lognai/plugin-sdk';
import { hasTaskAssign } from '../../utils/permissions';

export interface AssignFromMentionChipProps {
  mentionedUsers: MentionedUser[];
  currentAssigneeUserId?: string;
  onAssign: (mentionedUser: MentionedUser) => void;
  disabled?: boolean;
}

export const AssignFromMentionChip: React.FC<AssignFromMentionChipProps> = ({
  mentionedUsers,
  currentAssigneeUserId,
  onAssign,
  disabled,
}) => {
  const currentUser = getCurrentUser();
  if (!hasTaskAssign(currentUser?.permissions)) return null;

  const candidates = mentionedUsers.filter(
    (mentionedUser) => mentionedUser.userId !== currentAssigneeUserId
  );
  if (candidates.length === 0) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {candidates.map((mentionedUser) => (
        <Button
          key={mentionedUser.userId}
          type="button"
          size="sm"
          variant="outline"
          disabled={disabled}
          onClick={() => onAssign(mentionedUser)}
        >
          <UserPlus className="mr-1.5 h-3.5 w-3.5" />
          Assign to {mentionedUser.label}
        </Button>
      ))}
    </div>
  );
};
